import { IPublicClientApplication } from '@azure/msal-browser';
import { getTokenWithRedirectFallback } from '@tq/auth-components';
import axios, { AxiosHeaders, AxiosProgressEvent } from 'axios';
import { UploadToStorageRequest } from 'components/FileDragDrop/types';
import { parseHeader, parseStorageUploadRequest } from 'components/FileDragDrop/util';
import { DOCUMENT_STORE_TENANT_ID } from 'config';

import {
  b2cAuthority,
  b2cPolicyImpersonate,
  b2cPolicySignIn,
  b2cScope,
  baseApiUrl,
  tenantId,
} from '../../tools';
import { handleError } from './apiError';

let pca: IPublicClientApplication;
export const setPca = (publicClientApplication: IPublicClientApplication): void => {
  pca = publicClientApplication;
};

export const DEFAULT_API_FETCH_OPTIONS: Parameters<typeof fetch>[1] = {
  headers: {
    'TQ-Tenant': `${tenantId}`,
    'Content-Type': 'application/json',
  },
};

export const FORM_API_FETCH_OPTIONS: Parameters<typeof fetch>[1] = {
  headers: {
    'TQ-Tenant': `${tenantId}`,
  },
};

export const apiGet = <TResponse, TParams = void>(
  url: string,
  params?: TParams
): Promise<TResponse> => {
  const queryParams = toQueryParams(params);
  return apiFetch<TResponse>(queryParams ? `${url}?${queryParams}` : url);
};

export const apiGetFile = <TParams = void>(url: string, params?: TParams): Promise<Blob> => {
  const queryParams = toQueryParams(params);
  return apiFetchBlob(!queryParams ? url : `${url}?${queryParams}`);
};

export const apiPost = <TResponse, TBody>(url: string, body?: TBody): Promise<TResponse> => {
  return apiFetch<TResponse>(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : null,
  });
};

export const apiFormDataPost = <TResponse, FormData extends BodyInit | null | undefined>(
  url: string,
  form: FormData
): Promise<TResponse> => {
  return apiFetchForm<TResponse>(url, {
    method: 'POST',
    body: form,
  });
};

export const apiPut = <TResponse, TBody>(url: string, body?: TBody): Promise<TResponse> => {
  return apiFetch<TResponse>(url, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : null,
  });
};

export const apiDelete = <TResponse>(url: string): Promise<TResponse> => {
  return apiFetch<TResponse>(url, {
    method: 'DELETE',
  });
};

export const apiFetchForm = async <TResponse>(
  url: Request | string,
  init?: RequestInit | undefined
): Promise<TResponse> => {
  const token = await getToken();
  return fetch(`${baseApiUrl}${url}`, {
    ...init,
    headers: {
      ...(FORM_API_FETCH_OPTIONS?.headers ?? {}),
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
    .then((res) => {
      if (res.ok) {
        return getBody(res) as unknown as TResponse;
      }
      return handleError<TResponse>(res);
    })
    .catch((error) => {
      return Promise.reject(`An unknown error occured: ${error}`);
    });
};

export const apiFetch = async <TResponse>(
  url: Request | string,
  init?: RequestInit | undefined
): Promise<TResponse> => {
  const token = await getToken();
  return fetch(`${baseApiUrl}${url}`, {
    ...init,
    headers: {
      ...(DEFAULT_API_FETCH_OPTIONS?.headers ?? {}),
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
    .then((res) => {
      if (res.ok) {
        return getBody(res) as unknown as TResponse;
      }
      return handleError<TResponse>(res);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const apiFetchBlob = async (
  url: Request | string,
  init?: RequestInit | undefined
): Promise<Blob> => {
  const token = await getToken();
  return fetch(`${baseApiUrl}${url}`, {
    ...init,
    headers: {
      'content-type': 'text/csv;charset=UTF-8',
      ...(DEFAULT_API_FETCH_OPTIONS?.headers ?? {}),
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
    .then((res) => {
      if (res.ok) {
        return res.blob();
      }
      return handleError<Blob>(res);
    })
    .catch((error) => {
      return Promise.reject(`An unknown error occured: ${error}`);
    });
};

const getBody = <T>(res: Response): Promise<T> => {
  const paginationInfo = parsePaginationHeader(res);
  const responsePromise = res.text().then((data) => {
    if (isValidJson(data)) {
      return {
        ...JSON.parse(data),
        ...(paginationInfo && { paginationInfo }),
      };
    }
    return Promise.resolve(data);
  });
  return responsePromise.catch(() => Promise.reject({}));
};

const isValidJson = (text: string): boolean => {
  if (typeof text !== 'string') {
    return false;
  }
  try {
    const json = JSON.parse(text);
    return typeof json === 'object';
  } catch (error) {
    return false;
  }
};

export const getToken = async (): Promise<string | undefined> => {
  if (!pca) return undefined;
  const allAccounts = pca.getAllAccounts();
  pca.setActiveAccount(allAccounts.length > 0 ? allAccounts[0] : null);
  if (allAccounts.length > 0) {
    return getTokenWithRedirectFallback(pca, b2cScope, {
      authority: b2cAuthority,
      signInPolicyPath: b2cPolicySignIn,
      impersonatePolicyPath: b2cPolicyImpersonate,
    });
  }
};

const parsePaginationHeader = (res: Response) => {
  const paginationHeader = res.headers?.get('x-pagination');
  return paginationHeader ? JSON.parse(paginationHeader) : null;
};

const toQueryParams = <T>(params: T): string | null => {
  if (!params) {
    return null;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const arrayValue of value) {
        searchParams.append(key, arrayValue);
      }
    } else {
      searchParams.append(key, value);
    }
  }
  return Array.from(searchParams.keys()).length ? searchParams.toString() : null;
};

export const deleteFileFromStorageApi = async (uri: string): Promise<Response> => {
  try {
    const token = await getToken();
    const { data } = await axios.delete(`${baseApiUrl}documents`, {
      headers: {
        ...(DEFAULT_API_FETCH_OPTIONS?.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      } as unknown as AxiosHeaders,
      data: JSON.stringify({
        documentStoreTenantId: DOCUMENT_STORE_TENANT_ID,
        documentUrl: uri,
      }),
    });
    return data;
  } catch (error) {
    return Promise.reject(`An unknown error occured: ${error}`);
  }
};

export type UploadToStorageApiRequest = {
  uploadToStorageRequest: UploadToStorageRequest;
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
};

export const uploadFileToStorageApi = async (
  uploadToStorageRequest: UploadToStorageRequest
): Promise<string> => {
  const parsedUploadToStorageRequest = parseStorageUploadRequest(uploadToStorageRequest);
  try {
    const token = await getToken();
    return await axios
      .post(`${baseApiUrl}documents`, parsedUploadToStorageRequest, {
        onUploadProgress: uploadToStorageRequest.onUploadProgress,
        headers: {
          ...(FORM_API_FETCH_OPTIONS?.headers ?? {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'multipart/form-data',
        } as unknown as AxiosHeaders,
      })
      .then((res) => parseHeader(res, 'location'));
  } catch (error) {
    return Promise.reject(`An unknown error occured: ${error}`);
  }
};
